import { useEffect, useId, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  CLOSED_TO_VISITORS_MESSAGE,
  VISITOR_ACCESS_HINT,
  isAiResidentActorId,
  type PropertyPublic,
  type VisitorAccess,
} from "@townsy/contracts";
import { errorMessage } from "../api/client";
import { postSendVisitorsOut, putStoreAccess } from "../api/endpoints";
import { queryKeys, useBootstrapQuery } from "../api/queries";
import { useTownSession } from "../app/TownSessionContext";
import { propertyDisplayName } from "../lib/propertyLabels";
import { useTownStore, type ActorSummary } from "../state/store";
import { MyStoreSetupButton } from "./storeSetup/StoreSetupEntry";
import { MyStoreOffersButton } from "./rewards/offers/OfferSetupEntry";

/**
 * Open / Closed homes and stores (ADR-0019, founder request 2026-10-13: "an option to leave their property 'open' to
 * others, or 'closed'. If you want to invite friends over, you'd open it then close it once people are in").
 *
 * The owner's switch and "Send visitors out" (homes: My home; stores: the owner's store panel and the 2D directory's
 * "My stores"), and what visitors see. The server decides everything: the switch only shows the server's answer, the
 * Enter buttons are a convenience (the room-ticket API and the rooms apply the same rule), and the visitor count comes
 * only from the room state the owner's own client already has (when the owner is inside; unknown otherwise).
 */

/** The signed-in viewer is the property's current owner (public owner actor id; the server re-checks every command). */
export function isPropertyOwner(item: Pick<PropertyPublic, "ownerActorId">, viewerActorId: string | null | undefined): boolean {
  return Boolean(viewerActorId) && item.ownerActorId !== null && item.ownerActorId === viewerActorId;
}

/** A visitor (not the owner) looking at a closed home or store: Enter is disabled with CLOSED_TO_VISITORS_MESSAGE. */
export function closedToViewer(item: Pick<PropertyPublic, "access" | "ownerActorId">, viewerActorId: string | null | undefined, ownsIt = false): boolean {
  return item.access === "closed" && !ownsIt && !isPropertyOwner(item, viewerActorId);
}

/** The viewer's public actor id (guests included; only an account can own anything). */
export function useViewerActorId(): string | null {
  const fromTown = useTownStore((s) => s.bootstrap?.actor.actorId ?? null);
  const boot = useBootstrapQuery(!fromTown);
  return fromTown ?? boot.data?.actor.actorId ?? null;
}

/** Other people in the room (not the viewer, not AI residents). */
export function countOthers(actors: Readonly<Record<string, ActorSummary>>): number {
  let n = 0;
  for (const a of Object.values(actors)) if (!a.isLocal && !isAiResidentActorId(a.actorId)) n++;
  return n;
}

/** How many other people are inside `propertyId` right now, from the room the viewer is in; null when not inside it. */
export function useVisitorsInside(propertyId: string | null): number | null {
  const inside = useTownStore((s) => propertyId !== null && s.room.kind === "interior" && s.room.propertyId === propertyId);
  const others = useTownStore((s) => countOthers(s.actors));
  return inside ? others : null;
}

const ACCESS_LABEL: Record<VisitorAccess, string> = { open: "Open", closed: "Closed" };

/** The owner's Open / Closed switch with one line each (shows the server's answer; the parent sends the change). */
export function VisitorAccessSwitch({ access, disabled, onChange }: { access: VisitorAccess; disabled: boolean; onChange(next: VisitorAccess): void }) {
  const name = useId();
  return (
    <fieldset className="choice-group" data-movement-block="true" disabled={disabled} data-testid="visitor-access">
      <legend>Visitors</legend>
      <div className="choice-options" role="radiogroup" aria-label="Open or closed to visitors">
        {(["open", "closed"] as const).map((value) => (
          <label key={value} className="choice choice-pill" data-testid={`access-${value}`}>
            <input
              type="radio"
              className="choice-input"
              name={name}
              value={value}
              checked={access === value}
              onChange={() => onChange(value)}
              data-testid={`access-${value}-input`}
            />
            <span className="choice-text">{ACCESS_LABEL[value]}</span>
          </label>
        ))}
      </div>
      <ul className="small" style={{ margin: "6px 0 0", paddingLeft: 18 }} data-testid="visitor-access-hints">
        {(["open", "closed"] as const).map((value) => (
          <li key={value} style={{ fontWeight: access === value ? 700 : 400 }}>
            {VISITOR_ACCESS_HINT[value]}
          </li>
        ))}
      </ul>
    </fieldset>
  );
}

/** Why "Send visitors out" is disabled, or null (unknown count: enabled). */
export function sendOutDisabledReason(visitorsInside: number | null): string | null {
  return visitorsInside === 0 ? "Nobody else is inside right now." : null;
}

/** "Send visitors out": disabled with a reason when the owner can see that nobody else is inside. */
export function SendVisitorsOutView({ visitorsInside, busy, onSend }: { visitorsInside: number | null; busy: boolean; onSend(): void }) {
  const reason = sendOutDisabledReason(visitorsInside);
  const line =
    visitorsInside === null
      ? "Moves everyone else inside to the street at the entrance."
      : reason
        ? reason
        : `${visitorsInside} ${visitorsInside === 1 ? "visitor" : "visitors"} inside. They will be moved to the street at the entrance.`;
  return (
    <div className="row small" style={{ gap: 8, alignItems: "center", marginTop: 6 }}>
      <button type="button" className="btn btn-sm" disabled={busy || Boolean(reason)} title={reason ?? undefined} onClick={onSend} data-testid="send-visitors-out">
        Send visitors out
      </button>
      <span className="hint" data-testid="visitors-inside">
        {line}
      </span>
    </div>
  );
}

/** What a visitor reads about a closed home or store (panel and directory). */
export function ClosedNotice() {
  return (
    <p className="notice notice-info small" role="note" style={{ margin: 0 }} data-testid="closed-notice">
      {CLOSED_TO_VISITORS_MESSAGE}
    </p>
  );
}

export const SENT_OUT_MESSAGE = "Visitors inside are being moved to the street at the entrance.";

/** Success line after the owner changed the switch. */
export function accessChangedMessage(kind: "home" | "store", access: VisitorAccess): string {
  return access === "closed"
    ? `Your ${kind} is closed: nobody new can come in. People inside can stay.`
    : `Your ${kind} is open: anyone can come in.`;
}

type Message = { tone: "success" | "danger"; text: string };

/**
 * A store's Open / Closed switch and "Send visitors out" for its current owner (the store panel and the 2D directory).
 * Shown only when the caller has checked ownership from the API (`edit_interior` on the owner's own property read, or
 * the public owner id); the server re-checks every change and refuses anyone else.
 */
export function StoreVisitorsControl({ item }: { item: PropertyPublic }) {
  const session = useTownSession();
  const qc = useQueryClient();
  const visitorsInside = useVisitorsInside(item.id);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<Message | null>(null);
  // The server's answer is shown at once; the property read catches up (and wins once it does).
  const [confirmed, setConfirmed] = useState<VisitorAccess | null>(null);
  useEffect(() => setConfirmed(null), [item.access]);
  const access = confirmed ?? item.access;

  const refresh = async () => {
    await session?.refreshProperty(item.id);
    void qc.invalidateQueries({ queryKey: queryKeys.properties });
    void qc.invalidateQueries({ queryKey: queryKeys.property(item.id) });
  };
  const change = async (next: VisitorAccess) => {
    if (busy || next === access) return;
    setBusy(true);
    setMessage(null);
    try {
      const res = await putStoreAccess(item.id, { access: next });
      setConfirmed(res.access);
      setMessage({ tone: "success", text: accessChangedMessage("store", res.access) });
      await refresh();
    } catch (err) {
      setMessage({ tone: "danger", text: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  };
  const sendOut = async () => {
    if (busy) return;
    setBusy(true);
    setMessage(null);
    try {
      await postSendVisitorsOut(item.id);
      setMessage({ tone: "success", text: SENT_OUT_MESSAGE });
    } catch (err) {
      setMessage({ tone: "danger", text: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="stack" style={{ gap: 4 }} data-testid={`store-visitors-${item.id}`}>
      <VisitorAccessSwitch access={access} disabled={busy} onChange={(next) => void change(next)} />
      <SendVisitorsOutView visitorsInside={visitorsInside} busy={busy} onSend={() => void sendOut()} />
      {message ? (
        <div className={`notice notice-${message.tone} small`} role="status" data-testid="store-visitors-message">
          {message.text}
        </div>
      ) : null}
    </div>
  );
}

/**
 * "My stores" on the 2D directory (and the no-3D fallback): the stores the signed-in viewer owns, each with its Open /
 * Closed switch and "Send visitors out", so a store can be opened and closed without WebGL.
 */
export function DirectoryMyStores({ items }: { items: readonly PropertyPublic[] }) {
  const viewer = useViewerActorId();
  const signedIn = Boolean(useBootstrapQuery().data?.session.userId);
  const mine = signedIn ? items.filter((p) => p.type === "COMMERCIAL" && p.status === "OWNED" && isPropertyOwner(p, viewer)) : [];
  if (mine.length === 0) return null;
  return (
    <section className="stack" aria-labelledby="my-stores-title" id="my-stores" data-testid="directory-my-stores">
      <h2 id="my-stores-title">My stores</h2>
      {mine.map((p) => (
        <div key={p.id} className="section" data-testid={`my-store-${p.id}`}>
          <div className="row" style={{ justifyContent: "space-between", alignItems: "center" }}>
            <div className="small">
              <strong>{propertyDisplayName(p)}</strong> · {p.address}
            </div>
            {/* ADR-0020: name, logo, website, description and the sign inside on one screen; ADR-0027: offers likewise. */}
            <div className="row" style={{ gap: 6 }}>
              <MyStoreOffersButton propertyId={p.id} />
              <MyStoreSetupButton propertyId={p.id} />
            </div>
          </div>
          <StoreVisitorsControl item={p} />
        </div>
      ))}
    </section>
  );
}
